import { Alert } from '@mantine/core';
import { useTranslation } from '@refinedev/core';
import { IconRefreshAlert } from '@tabler/icons-react';
import { useEffect, useState, type ReactElement } from 'react';

export const VERSION_CHECK_INTERVAL_MS = 60_000;
export const VERSION_MANIFEST_PATH = '/version.json';

const RUNNING_FRONTEND_VERSION =
  import.meta.env.VITE_PROJECT_VERSION || 'development';
const VERSION_CHECK_DISABLED_VALUES = new Set(['development', 'unknown']);

type VersionManifest = {
  version: string;
};

const isVersionManifest = (value: unknown): value is VersionManifest =>
  typeof value === 'object' &&
  value !== null &&
  'version' in value &&
  typeof value.version === 'string' &&
  value.version.length > 0;

export const getDeployedFrontendVersion = async (
  signal?: AbortSignal
): Promise<string> => {
  const response = await fetch(VERSION_MANIFEST_PATH, {
    cache: 'no-store',
    credentials: 'same-origin',
    signal,
  });

  if (!response.ok) {
    throw new Error(`Version check failed with status ${response.status}`);
  }

  const manifest: unknown = await response.json();
  if (!isVersionManifest(manifest)) {
    throw new Error('Version check returned an invalid manifest');
  }

  return manifest.version;
};

type UpdateBannerProps = {
  currentVersion?: string;
  checkIntervalMS?: number;
};

export const UpdateBanner = ({
  currentVersion = RUNNING_FRONTEND_VERSION,
  checkIntervalMS = VERSION_CHECK_INTERVAL_MS,
}: UpdateBannerProps): ReactElement | null => {
  const { translate } = useTranslation();
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);

  useEffect(() => {
    if (VERSION_CHECK_DISABLED_VALUES.has(currentVersion)) return;

    const abortController = new AbortController();
    let isActive = true;
    let versionCheckSequence = 0;

    const checkVersion = async () => {
      const requestSequence = ++versionCheckSequence;

      try {
        const deployedVersion = await getDeployedFrontendVersion(
          abortController.signal
        );

        if (isActive && requestSequence === versionCheckSequence) {
          setIsUpdateAvailable(deployedVersion !== currentVersion);
        }
      } catch {
        // Keep the last known state when the manifest cannot be reached.
      }
    };
    const requestVersionCheck = () => void checkVersion();

    requestVersionCheck();
    const intervalID = window.setInterval(requestVersionCheck, checkIntervalMS);
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        requestVersionCheck();
      }
    };

    window.addEventListener('focus', requestVersionCheck);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isActive = false;
      abortController.abort();
      window.clearInterval(intervalID);
      window.removeEventListener('focus', requestVersionCheck);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkIntervalMS, currentVersion]);

  if (!isUpdateAvailable) {
    return null;
  }

  return (
    <Alert
      color="yellow"
      icon={<IconRefreshAlert />}
      radius={0}
      role="status"
      styles={{
        root: {
          position: 'sticky',
          top: 'var(--app-shell-header-height, 3.75rem)',
          zIndex: 198,
        },
        message: {
          fontWeight: 600,
          textAlign: 'center',
        },
      }}
      data-testid="update-banner"
    >
      {translate('components.updateBanner.message')}
    </Alert>
  );
};
